import type { JSX } from 'react';

import type { ErrorLike } from '@apollo/client';
import { AlertCircle } from 'lucide-react';

import { graphQLErrorsOf, networkErrorOf } from '../../utils/apolloErrors';
import { Button } from '../Button/Button';
import { Alert, AlertDescription } from '../Alert/Alert';

import styles from './ErrorDisplay.module.css';

interface ErrorDisplayProps {
  /**
   * The error object from Apollo Client
   */
  error: ErrorLike;
  /**
   * The main error message to display to the user
   */
  message: string;
  /**
   * Function to call when the retry button is clicked
   */
  onRetry: () => Promise<void> | void;
  /**
   * Optional additional CSS classes for the retry button
   */
  retryButtonClassName?: string;
  /**
   * Whether to show the technical details section
   * @default true
   */
  showTechnicalDetails?: boolean;
}

/**
 * A reusable error display component that shows GraphQL errors with technical details
 * and a retry button. Provides a consistent error experience across the admin app.
 */
export function ErrorDisplay({
  error,
  message,
  onRetry,
  retryButtonClassName,
  showTechnicalDetails = true,
}: ErrorDisplayProps): JSX.Element {
  const handleRetry = async () => {
    await onRetry();
  };

  return (
    <Alert variant="destructive">
      <AlertCircle className={styles.AlertIcon} size={16} />
      <AlertDescription>
        <div>{message}</div>
        {showTechnicalDetails && (
          <details className={styles.TechnicalDetails}>
            <summary>Technical Details (Click to expand)</summary>
            <pre className={styles.TechnicalPre}>{error.message}</pre>
            {networkErrorOf(error) && (
              <div className={styles.TechnicalSection}>
                <strong>Network Error:</strong> {error.message}
              </div>
            )}
            {graphQLErrorsOf(error).length > 0 && (
              <div className={styles.TechnicalSection}>
                <strong>GraphQL Errors:</strong>
                {graphQLErrorsOf(error).map((err, i) => (
                  <div key={i}>• {err.message}</div>
                ))}
              </div>
            )}
          </details>
        )}
        <Button
          variant="outline"
          size="sm"
          className={retryButtonClassName}
          onClick={handleRetry}
        >
          Retry
        </Button>
      </AlertDescription>
    </Alert>
  );
}

export default ErrorDisplay;
